import {
  Box,
  Button,
  Card,
  FormControl,
  Typography,
  Link,
} from "@mui/material";
import { useAppSelector } from "hooks/use-app-select";
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import Logo from "assets/images/side_nav_inkfree_logo.svg";
import { mobile, signer, signerAccentBar } from "theme/colors";
import { authRef, refFont } from "./AuthReferenceStyles";
import history from "utils/history";
import { verifyTOTP } from "screens/Auth/AuthService";
import { MuiOtpInput } from "mui-one-time-password-input";
import {
  convertResourceToObjectFormat,
  isTruthy,
  isValidOTP,
  openErrorNotification,
} from "helpers/methods";
import { loginAction, selectAuthenticated } from "redux/authSlice";
import { getAccountDisplayName } from "./authSession";
import { useAppDispatch } from "hooks/use-app-dispatch";
import { useAuthRouteRedirection } from "layouts/Config";
import viewpaths from "global/constants/ViewPathConstants";
import defaultImage from "assets/icons1/avatars/default.png";
import { useTitle } from "utils/UseTitle";
import title from "global/constants/TitleConstants";
import { updatePreference } from "redux/preferencesSlice";
import { buildBaseLoginPayload, handlePasswordExpiryRedirect } from "./Login";

interface CustomProps {
  location: any;
}

const OTP = (props: CustomProps) => {
  useTitle(title.VERITY_OTP);
  const dispatch = useAppDispatch();
  const email = props.location.state?.email ?? "";
  const isLoggedIn = useAppSelector(selectAuthenticated);
  const authRoute = useAuthRouteRedirection();
  const redirectionParam = new URLSearchParams(useLocation().search).get(
    "redirectionUrl",
  );
  const decodedUrl = redirectionParam
    ? decodeURIComponent(redirectionParam)
    : undefined;
  // Backing out of MFA mid-MCP-connection shouldn't drop the destination —
  // carry it back onto the login page's own redirectionUrl param.
  const backToLoginPath = decodedUrl
    ? `${viewpaths.loginViewPath}?redirectionUrl=${encodeURIComponent(decodedUrl)}`
    : viewpaths.loginViewPath;
  const [payload, setPayload] = useState({
    email: email,
    otp: "",
  });
  const [processing, setProcessing] = useState<boolean>(false);

  // Authenticator codes roll on a fixed 30s window, so derive the time left
  // from the wall clock rather than counting down from a guess.
  const totpSecondsLeft = () => 30 - (Math.floor(Date.now() / 1000) % 30);
  const [secondsLeft, setSecondsLeft] = useState<number>(totpSecondsLeft);

  useEffect(() => {
    const timerId = setInterval(() => setSecondsLeft(totpSecondsLeft()), 1000);
    return () => clearInterval(timerId);
  }, []);

  const verifyOTP = async () => {
    try {
      if (!isValidOTP(payload.otp)) {
        return openErrorNotification("Please enter the code");
      }
      setProcessing(true);
      const response = await verifyTOTP(payload.email, parseInt(payload.otp));
      const formattedResources = convertResourceToObjectFormat(
        response.resources,
      );
      if (!isTruthy(formattedResources)) {
        openErrorNotification("Something went wrong!");
        setProcessing(false);
        return;
      }

      const baseLoginPayload = buildBaseLoginPayload(response);

      if (handlePasswordExpiryRedirect(response, baseLoginPayload, dispatch))
        return;
      dispatch(
        loginAction({
          authenticated: true,
          accessToken: response.authToken,
          refreshToken: response.refreshToken,
          userName: response.name,
          email: (response.email ?? "").toLowerCase(),
          userRole: response.role,
          companyId: response.companyId,
          resources: formattedResources,
          profileImage: response.profilePicture
            ? "data:image/png;base64," + response.profilePicture
            : defaultImage,
          account: response.account,
          accountDisplayName: getAccountDisplayName(response),
          userId: response.id,
          isTrial: response.trial,
        }),
      );
      dispatch(
        updatePreference({
          layout: "vertical",
          theme: "dark",
        }),
      );
      setProcessing(false);
    } catch (error: any) {
      setProcessing(false);
      openErrorNotification(error.message);
    }
  };

  const handleKeypress = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      verifyOTP();
    }
  };

  const renderOTPScreen = () => {
    return (
      <Box
        component="main"
        id="main-content"
        tabIndex={-1}
        sx={{
          minHeight: "100vh",
          backgroundColor: authRef.pageBg,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
          py: 5,
          boxSizing: "border-box",
          outline: "none",
        }}
      >
        {/* Brand accent bar across the top of the canvas, per reference 3g. */}
        <Box
          aria-hidden="true"
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            height: "5px",
            background: signerAccentBar,
            zIndex: (theme) => theme.zIndex.appBar,
          }}
        />

        <Card
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: 460,
            borderRadius: "16px",
            border: `1px solid ${authRef.cardBorder}`,
            boxShadow: authRef.cardShadow,
            padding: { xs: "28px 20px 24px", sm: "40px 44px 36px" },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            boxSizing: "border-box",
          }}
        >
          <Box
            component="img"
            src={Logo}
            alt="Inkfree e-Sign"
            sx={{ width: 106, height: "auto", display: "block" }}
          />

          {/* Shield badge */}
          <Box
            aria-hidden="true"
            sx={{
              width: 56,
              height: 56,
              mt: 3,
              borderRadius: "50%",
              backgroundColor: authRef.badgeBgPurple,
              color: signer.purple.main,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <svg
              width="26"
              height="26"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 3l7 3v5c0 4.5-3 8.2-7 9.5-4-1.3-7-5-7-9.5V6l7-3z" />
              <path d="M9.5 12l2 2 3.5-3.5" />
            </svg>
          </Box>

          <Typography
            component="h1"
            sx={{
              color: mobile.base[500],
              fontSize: refFont.cardHeading,
              fontWeight: 700,
              textAlign: "center",
              mt: 2,
            }}
          >
            Two-factor check
          </Typography>

          <Typography
            sx={{
              color: authRef.subText,
              fontSize: refFont.cardBody,
              lineHeight: 1.6,
              textAlign: "center",
              mt: 1,
            }}
          >
            Enter the 6-digit code from your authenticator app.
          </Typography>

          <Box sx={{ width: "100%", mt: 3 }}>
            <FormControl
              sx={{
                width: "100%",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <MuiOtpInput
                length={6}
                onChange={(value: any) =>
                  setPayload({
                    ...payload,
                    otp: value.replace(/[^0-9]/g, ""),
                  })
                }
                sx={{
                  justifyContent: "center",
                  gap: { xs: "6px", sm: "9px" },
                  "& .MuiInputBase-root": {
                    width: "100%",
                    maxWidth: "46px",
                    minWidth: "34px",
                    height: { xs: "48px", sm: "54px" },
                    borderRadius: "8px",
                    backgroundColor: authRef.inputBg,
                    "& fieldset": {
                      borderColor: authRef.inputBorder,
                      borderWidth: "1px",
                    },
                    "&:hover fieldset": {
                      borderColor: authRef.inputBorderHover,
                    },
                    // Active box inverts to white with a brand-orange edge.
                    "&.Mui-focused": {
                      backgroundColor: "common.white",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: signer.orange.main,
                      borderWidth: "1.5px",
                    },
                  },
                  "& .MuiInputBase-input": {
                    padding: 0,
                    textAlign: "center",
                    fontSize: "20px",
                    fontWeight: 700,
                    color: mobile.base[500],
                  },
                }}
                value={payload.otp}
                onKeyPress={handleKeypress}
                TextFieldsProps={{
                  variant: "outlined",
                  type: "number",
                  inputProps: {
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                    style: {
                      MozAppearance: "textfield",
                    },
                    "aria-required": true,
                    "aria-label": "One time password input",
                  },
                  sx: {
                    "& input[type=number]": {
                      MozAppearance: "textfield",
                    },
                    "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
                      {
                        WebkitAppearance: "none",
                        margin: 0,
                      },
                  },
                }}
              />
            </FormControl>

            <Typography
              aria-live="off"
              sx={{
                fontSize: refFont.link,
                color: authRef.metaText,
                textAlign: "center",
                mt: 1.75,
              }}
            >
              Code refreshes in{" "}
              <Box
                component="span"
                sx={{ fontWeight: 700, color: mobile.base[500] }}
              >
                0:{String(secondsLeft).padStart(2, "0")}
              </Box>
            </Typography>
          </Box>

          <Button
            id="Verify OTP Code"
            fullWidth
            sx={{
              mt: 2.25,
              height: "48px",
              borderRadius: "8px",
              fontWeight: 700,
              textTransform: "none",
              fontSize: refFont.cardButton,
              letterSpacing: 0,
            }}
            type="submit"
            variant="contained"
            onClick={verifyOTP}
            disabled={processing}
            aria-label="Verify one time password"
          >
            Verify
          </Button>

          <Typography
            sx={{
              fontSize: refFont.label,
              color: authRef.subText,
              textAlign: "center",
              mt: 2.5,
            }}
          >
            <Link
              underline="hover"
              onClick={() => history.push(backToLoginPath)}
              sx={{
                color: signer.orange.main,
                fontSize: "inherit",
                fontWeight: 700,
                cursor: "pointer",
                textDecoration: "none",
                outline: "none",
                "&:hover": { textDecoration: "underline" },
                "&:focus-visible": {
                  outline: "2px solid",
                  outlineColor: signer.orange.main,
                  outlineOffset: "2px",
                },
              }}
              aria-label="Back to Login"
              tabIndex={0}
              onKeyPress={(e) => {
                if (e.key === "Enter" || e.key === " ")
                  history.push(backToLoginPath);
              }}
            >
              ← Back to log in
            </Link>
          </Typography>
        </Card>

        <Typography
          sx={{
            mt: 2.5,
            fontSize: refFont.cardFooter,
            color: authRef.footerText,
            textAlign: "center",
          }}
        >
          © {new Date().getFullYear()} Inkfree. All rights reserved.
        </Typography>
      </Box>
    );
  };

  if (isLoggedIn) {
    history.push(decodedUrl ?? authRoute);
    return null;
  }
  return renderOTPScreen();
};

export default OTP;
